"use client";

import { ButtonLink, TContentWithImageSection } from "@shared/cms";
import { Subtitle } from "@shared/ui";
import { imageFallback } from "@shared/utils";
import { cn } from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Markdown from "react-markdown";

function ImageComponent({ imageDesktop, imageMobile }: Pick<TContentWithImageSection, "imageDesktop" | "imageMobile">) {
  return (
    <>
      {imageDesktop && (
        <Image
          src={imageDesktop.url}
          width={imageDesktop.width || 580}
          height={imageMobile.height || 504}
          alt={imageDesktop.alternativeText || imageDesktop.name || ""}
          className="w-full h-auto object-contain hidden md:inline-block"
          onError={imageFallback()}
        />
      )}
      {imageMobile && (
        <Image
          src={imageMobile.url}
          width={imageMobile.width || 580}
          height={imageMobile.height || 504}
          alt={imageMobile.alternativeText || imageMobile.name || ""}
          className="w-full h-auto object-contain md:hidden"
          onError={imageFallback()}
        />
      )}
    </>
  );
}

export function ContentWithImageSection({
  title,
  imageDesktop,
  imageMobile,
  description,
  subtitle,
  button,
  direction,
  features,
}: TContentWithImageSection) {
  return (
    <>
      <section>
        <div className="container py-14 md:py-16">
          <div className="flex flex-col md:flex-row items-center md:gap-16 lg:gap-32">
            {imageDesktop && imageMobile && direction === "ltr" && (
              <div className="flex-1">
                <ImageComponent imageDesktop={imageDesktop} imageMobile={imageMobile} />
              </div>
            )}
            <div className="md:w-[22.5rem] lg:w-[28.5rem]">
              {subtitle && <Subtitle {...subtitle} containerClass="mb-4" />}
              <div>
                <div className="text-2xl sm:text-4xl font-bold text-gray-700">{title}</div>
                {description && <div className="text-base sm:text-lg text-gray-500 mt-4">{description}</div>}
              </div>
              {features && features.length > 0 && direction === "rtl" && (
                <>
                  {imageDesktop && imageMobile && (
                    <div className="sm:hidden mt-[2.875rem] mb-1">
                      <ImageComponent imageDesktop={imageDesktop} imageMobile={imageMobile} />
                    </div>
                  )}
                  <div className="mt-9 mb-8 sm:mt-10 sm:mb-10 space-y-8">
                    {features.map((feature) => {
                      return (
                        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-5" key={feature.id}>
                          {feature.icon && (
                            <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
                              <Image
                                src={feature.icon.url}
                                width={feature.icon.width || 24}
                                height={feature.icon.height || 24}
                                alt={feature.icon.alternativeText || feature.icon.name || ""}
                                className="size-5 lg:size-6 text-primary min-w-5 lg:min-w-6"
                                onError={imageFallback()}
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="font-bold text-gray-700 mb-2">{feature.title}</div>
                            <div className="text-sm sm:text-base text-gray-500">
                              <Markdown>{feature.description}</Markdown>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {button && (
                <div className="mt-8 sm:mt-10">
                  <ButtonLink {...button} size="sm" />
                </div>
              )}
            </div>
            {imageDesktop && imageMobile && direction === "rtl" && (
              <div className={cn("flex-1", features && features.length > 0 ? "hidden sm:block" : "")}>
                <ImageComponent imageDesktop={imageDesktop} imageMobile={imageMobile} />
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
